import React, { useState, useEffect } from 'react';
import { Bell, AlertCircle } from 'lucide-react';
import api from '../../api';

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function TeacherAnnouncementsPage() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/announcements')
      .then(d => { if (d.success) setAnnouncements(d.data); })
      .catch(() => setAnnouncements([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-5" data-testid="teacher-announcements-page">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Announcements</h1>
        <p className="text-sm text-slate-500 mt-0.5">Messages from the school administration</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-7 h-7 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : announcements.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
          <Bell className="w-10 h-10 text-slate-200 mx-auto mb-3" />
          <p className="font-medium text-slate-500">No announcements yet</p>
          <p className="text-sm text-slate-400 mt-1">School administration announcements will appear here</p>
        </div>
      ) : (
        <div className="space-y-3">
          {announcements.map(ann => (
            <div
              key={ann.id}
              className={`bg-white border rounded-xl p-5 ${ann.priority === 'urgent' ? 'border-red-200 bg-red-50/30' : 'border-slate-200'}`}
              data-testid={`ann-${ann.id}`}
            >
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg flex-shrink-0 ${ann.priority === 'urgent' ? 'bg-red-100' : 'bg-indigo-50'}`}>
                  {ann.priority === 'urgent'
                    ? <AlertCircle className="w-4 h-4 text-red-600" />
                    : <Bell className="w-4 h-4 text-indigo-600" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    {ann.priority === 'urgent' && (
                      <span className="bg-red-100 text-red-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">Urgent</span>
                    )}
                    <span className="text-xs text-slate-400">{timeAgo(ann.created_at)}</span>
                    {ann.created_by_name && (
                      <span className="text-xs text-slate-400">· {ann.created_by_name}</span>
                    )}
                  </div>
                  <h3 className="font-semibold text-slate-800">{ann.title}</h3>
                  <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">{ann.content}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
